import { useMemo } from 'react';
import { useOnboarding } from '@/components/onboarding/useOnboarding';
import { useTranslation } from '@/i18n/LanguageContext';
import type { WorkflowProfile } from '@/lib/workflowTemplates';
import { buildSystemPrompt, type ComposedPrompt } from './buildSystemPrompt';
import { buildWelcomeMessage } from './welcomeMessage';

export interface AiPersona {
  /** Composed 6-layer system prompt (stable prefix + volatile tail + full). */
  prompt: ComposedPrompt;
  /** First-message text for AiAssistantDialog when conversation is empty. */
  welcome: string;
  /** Hash of the stable prefix — useful for React `key`-based resets. */
  prefixHash: string;
}

/**
 * Cheap non-crypto hash for cache-busting a stable prefix.
 * Good enough for React key comparisons; not for security.
 */
function quickHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h.toString(36);
}

/**
 * Reactive hook that exposes the composed AI persona for the current
 * onboarding profile + optional per-workflow honing.
 *
 * Memoizes on onboarding.progress.preferences + workflowProfile + language,
 * so consumers (Index.tsx) can pass the returned `prompt.full` to
 * AiAssistantDialog's `aiHoningPrompt` prop without extra wiring.
 */
export function useAiPersona(
  workflowProfile: WorkflowProfile | null,
  basePrompt?: string,
): AiPersona {
  const { progress } = useOnboarding();
  const { language } = useTranslation();
  const preferences = progress?.preferences ?? null;

  return useMemo(() => {
    const prompt = buildSystemPrompt({ preferences, workflowProfile, lang: language, basePrompt });
    const welcome = buildWelcomeMessage(preferences, language);
    const prefixHash = quickHash(prompt.cacheablePrefix);
    return { prompt, welcome, prefixHash };
  }, [preferences, workflowProfile, language, basePrompt]);
}
